import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import type z from 'zod';

import { RadioGroup } from '@nesso/ui/primitives/radio-group';

import {
    noteLengthStepSchema,
    type StepProps,
    useUpdateOnboardingStepOptions,
} from '@/entities/practice-member';
import {
    notesSettingsOptions,
    useUpdateNotesSettingsOptions,
} from '@/entities/practice-member-settings';
import { BulletList } from '@/shared/ui/forms/bullet-list';
import { CardFormSection } from '@/shared/ui/forms/card-form-section';
import { RadioCardItem } from '@/shared/ui/forms/radio-card-item';

const DESCRIPTIONS = {
    BULLETED: {
        CONCISE: [
            'Reported increased anxiety/intrusive thoughts due to workload.',
            'Low adherence to "worry time."',
            'Sleep poor (5-6 hrs x3 nights).',
            'Conflict with coworker.',
        ],
        STANDARD: [
            'Reported feeling "overwhelmed" by workload and a regression in managing intrusive thoughts.',
            'Struggling to consistently use the "worry time" technique.',
            'Reported sleeping only 5-6 hours on three nights; links this to increased morning anxiety.',
            'Reported a tense interaction with a coworker that left them feeling "defensive and frustrated."',
        ],
    },
    NARRATIVE: {
        CONCISE:
            'Client reported feeling overwhelmed by work stress, resulting in increased intrusive thoughts and poor adherence to "worry time." Sleep was limited to 5-6 hours on three nights, which exacerbated morning anxiety. Client detailed a recent tense conflict with a coworker.',
        STANDARD:
            'Client reported feeling "overwhelmed" by their workload this week and noted a regression in their ability to manage intrusive thoughts. He stated he is still struggling to consistently implement the 15-minute "worry time" technique. He reported sleeping only 5-6 hours on three nights this week, which they link to increased morning anxiety. The client also reported a tense interaction with a coworker earlier in the week, which left them feeling "defensive and frustrated" for the rest of the day.',
    },
} as const;

export function OnboardingNoteLengthStep(props: StepProps) {
    const { data: notesSettings } = useSuspenseQuery(notesSettingsOptions());

    const form = useForm<z.infer<typeof noteLengthStepSchema>>({
        mode: 'onChange',
        resolver: zodResolver(noteLengthStepSchema),
        defaultValues: { noteLength: notesSettings.value.noteLength },
    });
    const {
        formState: { isDirty, isSubmitting, isSubmitted, isValid },
    } = form;

    const mutation = useMutation(useUpdateNotesSettingsOptions());
    const updateOnboardingStepMutation = useMutation(useUpdateOnboardingStepOptions());

    const onSubmit = async (data: z.infer<typeof noteLengthStepSchema>) => {
        try {
            if (props.nextStep) {
                updateOnboardingStepMutation.mutate(props.nextStep);
            }
            if (isDirty) {
                await mutation.mutateAsync(data);
            }
            props.onNext();
        } catch {
            console.error('Failed to update note length step');
        }
    };

    const handleManualSubmit = () => {
        form.handleSubmit(onSubmit)();
    };

    const getConciseDescription = () => {
        return notesSettings.value.noteFormat === 'BULLETED' ? (
            <BulletList items={DESCRIPTIONS.BULLETED.CONCISE} />
        ) : (
            DESCRIPTIONS.NARRATIVE.CONCISE
        );
    };
    const getStandardDescription = () => {
        return notesSettings.value.noteFormat === 'BULLETED' ? (
            <BulletList items={DESCRIPTIONS.BULLETED.STANDARD} />
        ) : (
            DESCRIPTIONS.NARRATIVE.STANDARD
        );
    };

    return (
        <FormProvider {...form}>
            <CardFormSection
                title="How much detail do you like in your notes?"
                description="Choose the length you prefer"
                progressValue={props.progress}
                isError={isSubmitted && !isValid}
                isFatalError={mutation.isError}
                disabledBackButton={isSubmitting}
                disabledNextButton={isSubmitting}
                onNext={handleManualSubmit}
                onBack={props.onBack}
            >
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Controller
                        control={form.control}
                        name="noteLength"
                        render={({ field }) => (
                            <RadioGroup
                                {...field}
                                onValueChange={field.onChange}
                                disabled={isSubmitting}
                                className="flex flex-col lg:flex-row gap-4 lg:gap-8 xl:gap-11 justify-center items-stretch lg:items-start"
                            >
                                <RadioCardItem
                                    value="CONCISE"
                                    title="Concise"
                                    description={getConciseDescription()}
                                />
                                <RadioCardItem
                                    value="STANDARD"
                                    title="Standard"
                                    description={getStandardDescription()}
                                />
                            </RadioGroup>
                        )}
                    />
                </form>
            </CardFormSection>
        </FormProvider>
    );
}
